import React, { useState, useEffect, useRef } from 'react'

// next components
import Image from 'next/image'
import Link from 'next/link'

// custom functions
import { useViewport } from 'utils'
import { calculateAuctionTimeLeft, categorizeNFT, formattedAmount, getTimeDifference } from 'utils/helpers'
import Lottie from 'lottie-react'
import NotifyAlarmIcon from 'assets/lottery/notify-alarm.json'

// images and icons
import SolanaLogoMobileIcon from 'assets/icons/solana-logo-gray-14x12.svg?component'
import SingleLiveIcon from 'assets/icons/single-live-icon.svg?component'
import EditionIcon from 'assets/icons/edition-icon.svg?component'
import PlayVideoIcon from 'assets/icons/play-video.svg'
import PauseVideoIcon from 'assets/icons/pause-video.svg'
import ThreeDotIcon from 'assets/icons/three-dot-icon.svg?component'
import NftCardMenuPage from 'components/Menu/NftCardMenuPage'

// styles
import styles from './index.module.scss'
import ImageWithFoster from 'components/ImageWithFoster'
import { getDisplayUserName } from 'utils/strings'
import SocialShareModal from 'components/Menu/SocialShareModal'
import NftCardMenuModal from 'components/Modal/NftCardMenuModal'
import useClickOutside from 'components/Menu/ClickOutsideMenu'

const LiveAuctionCard = props => {
  const { data, onClick, setCardExpired } = props
  const viewport = useViewport()
  const [timeLeft, setTimeLeft] = useState(null)
  const [play, setPlay] = useState(false)
  const [showNftMenu, setShowNftMenu] = useState(false)
  const [openShareModal, setOpenShareModal] = useState(false)
  const [auctionStarted, setAuctionStarted] = useState(true)
  const [hover, setHover] = useState(false)
  const nftWidth = 600
  const nftType = categorizeNFT(data.parent_nft, data.max_supply)

  useEffect(() => {
    if (
      data.asset_type.includes('video') &&
      (viewport === 'xs' || viewport === 'sm' || viewport === 'md') &&
      data.cover_image_url == null
    ) {
      let hidden_video: HTMLVideoElement = document.getElementById('hidden_video') as HTMLVideoElement
      hidden_video.currentTime = 3
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  useEffect(() => {
    if (
      data.start_time !== null &&
      data.start_time !== undefined &&
      data.end_time !== undefined &&
      data.end_time !== null
    ) {
      setTimeLeft(calculateAuctionTimeLeft(getStartTime(data)))
      const timer = setInterval(() => {
        let timeLeft = calculateAuctionTimeLeft(getStartTime(data))
        if (
          timeLeft['days'] === '00' &&
          timeLeft['hours'] === '00' &&
          timeLeft['minutes'] === '00' &&
          timeLeft['seconds'] === '00'
        )
          setCardExpired(data)
        setTimeLeft(timeLeft)
      }, 1000)

      return () => clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.start_time, data.end_time])

  const getStartTime = data => {
    let difference = getTimeDifference(data.start_time)

    if (difference >= 0) {
      setAuctionStarted(false)
      return data.start_time
    } else {
      setAuctionStarted(true)
      return data.end_time
    }
  }

  const isExpired = () => {
    if (
      timeLeft &&
      timeLeft['days'] === '00' &&
      timeLeft['hours'] === '00' &&
      timeLeft['minutes'] === '00' &&
      timeLeft['seconds'] === '00'
    )
      return true
    return false
  }

  const menuRef = useRef(null)

  useClickOutside(menuRef, () => {
    if (viewport === 'lg' || viewport === 'xl' || viewport === '2xl') {
      setShowNftMenu(false)
    }
  })

  return (
    <div className={styles.layout} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <div className={styles.container}>
        <div className={styles.imageArea}>
          <div className={styles.imageContent}>
            {data.asset_type.includes('video') ? (
              viewport === 'xs' || viewport === 'sm' || viewport === 'md' ? (
                <div className="w-full relative flex justify-center items-center overflow-hidden">
                  <Link href={`${window.origin}/nft/${data.token_id}`} passHref>
                    <a className="w-full h-full">
                      {play ? (
                        <div
                          className="w-full h-full absolute"
                          dangerouslySetInnerHTML={{
                            __html: `<video autoplay loop muted webkit-playsinline playsinline class="w-full h-full object-cover overflow-hidden">
              <source src=${data.asset_url} type="video/mp4" /></video>`,
                          }}
                          onClick={() => onClick()}
                        />
                      ) : data.cover_image_url != null && data.cover_image_url !== '' ? (
                        <ImageWithFoster
                          src={data.cover_image_url}
                          width={nftWidth}
                          onClick={onClick}
                          className="-w-full h-full object-contain"
                        />
                      ) : (
                        <div
                          className="w-full h-full absolute"
                          dangerouslySetInnerHTML={{
                            __html: `<video id="hidden_video" muted webkit-playsinline playsinline class="w-full h-full object-contain overflow-hidden">
              <source src=${data.asset_url} type="video/mp4" /></video>`,
                          }}
                          onClick={() => onClick()}
                        />
                      )}
                    </a>
                  </Link>
                  <div className={styles.playButton}>
                    <div className={`${play ? 'w-[18px]' : 'w-[22px]'} h-[18px] flex justify-end`}>
                      <Image
                        src={play ? PauseVideoIcon : PlayVideoIcon}
                        width={18}
                        height={18}
                        alt=""
                        onClick={() => setPlay(!play)}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <Link href={`${window.origin}/nft/${data.token_id}`} passHref>
                  <a className="w-full h-full">
                    <div
                      className="w-full h-full"
                      dangerouslySetInnerHTML={{
                        __html: `<video autoplay loop muted webkit-playsinline playsinline class="w-full h-full object-cover overflow-hidden">
                <source src=${data.asset_url} type="video/mp4" /></video>`,
                      }}
                      onClick={() => onClick()}
                    />
                  </a>
                </Link>
              )
            ) : data.asset_type.includes('audio') ? (
              <Link href={`${window.origin}/nft/${data.token_id}`} passHref>
                <a className="w-full h-full">
                  <ImageWithFoster
                    src={data.cover_image_url}
                    width={nftWidth}
                    className="w-full h-full object-contain"
                    onClick={() => onClick()}
                  />
                </a>
              </Link>
            ) : data.cover_image_url ? (
              <Link href={`${window.origin}/nft/${data.token_id}`} passHref>
                <a className="w-full h-full">
                  <ImageWithFoster
                    src={data.cover_image_url}
                    width={nftWidth}
                    onClick={onClick}
                    originalSrc={data.fallBack}
                    className="w-full h-full object-contain"
                  />
                </a>
              </Link>
            ) : (
              <Link href={`${window.origin}/nft/${data.token_id}`} passHref>
                <a className="w-full h-full">
                  <ImageWithFoster
                    src={data.asset_url}
                    width={nftWidth}
                    onClick={onClick}
                    originalSrc={data.fallBack}
                    className="w-full h-full object-contain"
                  />
                </a>
              </Link>
            )}
          </div>
        </div>

        <div className={`${styles.bidContainer}`}>
          <div className={styles.bidContentView}>
            <div className="w-full flex justify-between items-center">
              <div className="flex gap-[8px] items-center">
                {nftType === '1 of 1' ? <SingleLiveIcon /> : <EditionIcon />}
                <div className={styles.bidTime}>{nftType}</div>
              </div>

              {isExpired() ? (
                <></>
              ) : (
                <div>
                  <div className={styles.timeSection}>
                    <div className="flex items-center">
                      {viewport === 'lg' ? (
                        'Ends in'
                      ) : (
                        <div className="flex">
                          Live&nbsp;
                          <div className="w-[15px]">
                            <Lottie animationData={NotifyAlarmIcon} loop={true} />
                          </div>
                          &nbsp;Ends in
                        </div>
                      )}
                      &nbsp;
                      {timeLeft && timeLeft['days'] !== '00' && (
                        <div className="flex">
                          {timeLeft['days']}
                          <div>d</div>
                        </div>
                      )}
                      &nbsp;
                      {timeLeft && Number(timeLeft['hours']) > 0 && (
                        <div className="flex">
                          {timeLeft && timeLeft['hours']}
                          <div>h</div>
                        </div>
                      )}
                      {timeLeft && Number(timeLeft['days']) == 0 && Number(timeLeft['hours']) == 0 && (
                        <div className="flex">
                          {timeLeft['minutes']}
                          <div>m</div>
                          &nbsp;
                        </div>
                      )}
                      {timeLeft && Number(timeLeft['days']) == 0 && Number(timeLeft['hours']) == 0 && (
                        <div className="flex">
                          {timeLeft['seconds']}
                          <div>s</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div ref={menuRef} className="relative w-full flex flex-col mt-[8px]">
              <Link href={`/nft/${data.token_id}`} passHref>
                <a className="w-fit">
                  <div className={styles.nftName}>{data?.nft_name}</div>
                </a>
              </Link>

              {showNftMenu && (viewport === 'lg' || viewport === 'xl' || viewport === '2xl') && (
                <NftCardMenuPage
                  shareArtwork={() => {
                    setShowNftMenu(false)
                    setOpenShareModal(true)
                  }}
                  isOwn={false}
                  cardType={''}
                  hideArtwork={() => {}}
                  unhideArtwork={() => {}}
                  unSave={() => {}}
                />
              )}

              <div className="w-full flex items-center justify-between">
                <div className={styles.artistName}>
                  {data.artist_username?.length > 0 ? (
                    <Link href={`/${data.artist_username}`} passHref>
                      <a className="w-fit">{getDisplayUserName(data.artist_username)}</a>
                    </Link>
                  ) : (
                    <a className="w-fit">Ecosystem Artist</a>
                  )}
                </div>

                <div
                  className="w-[30px] h-[30px] flex justify-center items-center"
                  onClick={() => setShowNftMenu(!showNftMenu)}
                >
                  <div className={showNftMenu ? styles.ellipseContainer : styles.ellipseHover}>
                    <ThreeDotIcon alt="" />
                  </div>
                </div>
              </div>
            </div>

            <div>
              {isExpired() ? (
                <div className={styles.bidCountContainer}>
                  <div className={styles.bidCount}>{'Auction Ended'}</div>
                </div>
              ) : (
                <div className={styles.bidCountContainer}>
                  <div className={`h-full mr-[5px]`}>
                    <SolanaLogoMobileIcon alt="" />
                  </div>
                  <div className={styles.bidCount}>
                    {data.current_bid !== null && data.current_bid > 0
                      ? formattedAmount(data.current_bid)
                      : data.reserve_price !== null
                      ? formattedAmount(data.reserve_price)
                      : ''}
                  </div>
                  <div className={styles.bidName}>{data.current_bid !== null ? 'Current Bid' : 'Reserve Price'}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {openShareModal && (
        <SocialShareModal
          open={openShareModal}
          close={() => setOpenShareModal(false)}
          vote={true}
          message={'Check out this artist on Foster Marketplace @foster_protocol #fostermarketplace'}
          url={`/nft/${data.token_id}`}
        />
      )}

      {showNftMenu && (viewport === 'xs' || viewport === 'sm' || viewport === 'md') && (
        <NftCardMenuModal
          open={showNftMenu}
          onClose={() => setShowNftMenu(false)}
          isOwn={false}
          shareArtwork={() => {
            setShowNftMenu(false)
            setOpenShareModal(true)
          }}
          hideArtwork={() => {}}
          unhideArtwork={() => {}}
          unSave={() => {}}
          cardType={''}
        />
      )}
    </div>
  )
}

export default LiveAuctionCard
